import type { Color, Square } from '../game/types.js';
import type { Difficulty, Variant } from '../settings/types.js';

// v1: initial checkers schema. A "move" here is one complete legal turn — a
// quiet diagonal slide or an entire capture chain — produced by
// `MoveValidator.legalMoves` and consumed verbatim by `GameState.applyMove`.
// Bumping this version (or any breaking shape change) invalidates older slots.
export const HISTORY_SCHEMA_VERSION = 1;

/**
 * Who controlled each side at the time of the game. `ai` and `local` games
 * always start the local user as white; in `online` games the seed records
 * whichever color the local user was actually assigned by the server.
 */
export type RecordedMode = 'ai' | 'local' | 'online';

export interface RecordedMove {
  readonly player: Color;
  readonly from: Square;
  readonly to: Square;
  readonly path: readonly Square[];
  readonly captures: readonly Square[];
  readonly promoted: boolean;
}

export interface RecordedSeed {
  readonly mode: RecordedMode;
  readonly variant: Variant;
  readonly difficulty: Difficulty | null;
  readonly playerWhite: string;
  readonly playerBlack: string;
  readonly localColor: Color | null; // null for hot-seat local games
  readonly roomCode: string | null;
  readonly createdAt: number;
}

export interface ActiveSession {
  readonly id: string;
  readonly version: number;
  readonly seed: RecordedSeed;
  readonly moves: readonly RecordedMove[];
  readonly updatedAt: number;
}

export interface GameRecord {
  readonly id: string;
  readonly version: number;
  readonly createdAt: number;
  readonly finishedAt: number;
  readonly mode: RecordedMode;
  readonly variant: Variant;
  readonly players: { readonly white: string; readonly black: string };
  readonly winner: Color | null; // null => draw or abandoned
  readonly result: 'win' | 'draw' | 'abandoned';
  readonly seed: RecordedSeed;
  readonly moves: readonly RecordedMove[];
}

// Build a synthetic GameRecord from an ActiveSession — used for the online
// "View saved position" path when reconnect fails. winner is null and result
// is 'abandoned' because the game never reached game-over.
export function recordFromActiveSession(session: ActiveSession): GameRecord {
  return {
    id: session.id,
    version: HISTORY_SCHEMA_VERSION,
    createdAt: session.seed.createdAt,
    finishedAt: session.updatedAt,
    mode: session.seed.mode,
    variant: session.seed.variant,
    players: { white: session.seed.playerWhite, black: session.seed.playerBlack },
    winner: null,
    result: 'abandoned',
    seed: session.seed,
    moves: session.moves,
  };
}

export { generateId } from '@bn-games/history-core';
